import * as React from 'react';

import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import { Flex } from '@lightspeed/flame/Core';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@lightspeed/flame/Modal';
import { Select } from '@lightspeed/flame/Select';
import { Text } from '@lightspeed/flame/Text';

import Decimal from 'app/src/data/base/Decimal';
import Format from 'app/src/data/Format';
import { translate } from 'pages/utils/translate';
import Monitoring from 'shared_modules/Monitoring';

import { TipReAssignModalProps } from '.';
import { getAllEmployees, IEmployee } from './http';

enum ActionTypes {
  apiUpdating = 'apiUpdating',
  apiActionError = 'apiActionError',
}

type State = {
  [ActionTypes.apiUpdating]: boolean;
  [ActionTypes.apiActionError]: string | null;
};

interface ApiUpdating {
  type: ActionTypes.apiUpdating;
  payload: boolean;
}

interface ApiActionError {
  type: ActionTypes.apiActionError;
  payload: string | null;
}

type Action = ApiUpdating | ApiActionError;

function modalStateReducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.apiUpdating:
      return { ...state, apiUpdating: Boolean(action.payload) };
    case ActionTypes.apiActionError:
      return {
        ...state,
        apiActionError: action.payload,
      };
    default:
      throw new Error();
  }
}

export const TippingReassignModal: React.FC<TipReAssignModalProps> = ({
  onCancel,
  saleManager,
  currentSelectedEmployee,
  onTipReassignComplete,
}) => {
  const [modalLoadingState, dispatch] = React.useReducer(modalStateReducer, {
    apiActionError: null,
    apiUpdating: true,
  });

  const [tipEmployeeID, setTipEmployeeID] = React.useState(currentSelectedEmployee);
  const [listOfEmployees, setListOfEmployees] = React.useState<IEmployee[]>([]);

  React.useEffect(() => {
    const getEmployeeJson = async () => {
      try {
        const employees = await getAllEmployees();

        setListOfEmployees(employees);
      } catch (e) {
        Monitoring.addError(e, 'TipReassignModal', {
          errorName: 'tip.error.assign',
          description: 'Unable to fetch the list of employees or no employee were found',
        });

        setListOfEmployees([
          {
            employeeID: -1,
            firstName: translate('No employee found'),
            lastName: '',
          },
        ]);
      } finally {
        dispatch({ type: ActionTypes.apiUpdating, payload: false });
      }
    };

    getEmployeeJson();
  }, [setListOfEmployees]);

  const handleCancelTipReassign = () => {
    onCancel();
  };

  const handleTipReassign = React.useCallback(() => {
    dispatch({ type: ActionTypes.apiUpdating, payload: true });
    saleManager.sale.set('tipEmployeeID', tipEmployeeID);
    saleManager
      .updateSale()
      .done(() => {
        const tipAmount = new Decimal(saleManager.sale.get('calcTips'));
        const newTipEmployee = listOfEmployees.find(
          (employeeRow) => employeeRow.employeeID === tipEmployeeID
        );

        const alertText = `${translate(
          `Tip:  <var>${Format.formatMoney(tipAmount, 0)}</var>  Assigned to <var>${
            newTipEmployee?.firstName
          } ${newTipEmployee?.lastName}</var>`
        )}`;

        Monitoring.addEvent('tip.reassign', 'TipReassignModal', {
          description: 'Merchant re-assigned the tip to another employee',
          newTipEmployee,
          tipAmount: tipAmount?.toNumber() || null,
        });

        if ($('.tip-assignment-display').length > 0) {
          const sanitizedAlert = alertText.replace(/<(?:.|\n)*?>/gm, '');
          $('.tip-assignment-display').text(sanitizedAlert);
        } else {
          window.warnUser(alertText);
        }

        onTipReassignComplete();
      })
      .fail((e: Error) => {
        Monitoring.addError(e, 'TipReassignModal', {
          errorName: 'tip.error.assign',
          description: `Unable to re-assign tip to employee ${tipEmployeeID}`,
        });
        dispatch({
          type: ActionTypes.apiActionError,
          payload: translate(`Unable to re-assign tip`),
        });
      });
  }, [listOfEmployees, onTipReassignComplete, saleManager, tipEmployeeID]);

  const handleTipEmployeeSelection = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTipEmployeeID(Format.parseInt(e.currentTarget.value, 0));
  };

  return (
    <Modal isOpen={true} closeOnEsc={false}>
      <ModalHeader
        onCloseClicked={handleCancelTipReassign}
        showCloseButton={!modalLoadingState.apiUpdating}
        aria-label="Reassign tip heading"
      >
        {translate('Reassign tip')}
      </ModalHeader>

      <ModalBody>
        {modalLoadingState.apiActionError && (
          <Alert type="danger" mb={4} noCloseBtn aria-label="alert dialog">
            {modalLoadingState.apiActionError}
          </Alert>
        )}
        <Flex minWidth="412px" flexDirection="column">
          <Text fontWeight="bold">{translate('Select employee')}</Text>
          <Select
            value={tipEmployeeID}
            aria-label="Employee Selection Menu"
            onChange={handleTipEmployeeSelection}
            disabled={modalLoadingState.apiUpdating || listOfEmployees.length <= 1}
          >
            {listOfEmployees.map((employee) => (
              <option
                value={employee.employeeID}
                aria-label={`option ${employee.firstName}-${employee.lastName}`}
                key={employee.employeeID}
              >
                {employee.firstName} {employee.lastName}
                {currentSelectedEmployee === employee.employeeID &&
                  translate(' - Currently assigned')}
              </option>
            ))}
          </Select>
        </Flex>
      </ModalBody>

      <ModalFooter>
        <Flex justifyContent="flex-end">
          <Button mr={2} aria-label="cancel button" onClick={handleCancelTipReassign}>
            {translate('Cancel')}
          </Button>
          <Button
            id="tip_reassignReceiptScreen"
            aria-label="reassign button"
            onClick={handleTipReassign}
            fill
            variant="secondary"
            loading={modalLoadingState.apiUpdating}
            disabled={
              currentSelectedEmployee === tipEmployeeID ||
              modalLoadingState.apiActionError !== null ||
              listOfEmployees.length <= 1
            }
          >
            {translate('Reassign')}
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};
